import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API } from '../App';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { 
  Bell, X, Play, Sparkles, Zap, Heart, TrendingUp, 
  ChevronRight, CheckCircle, Crown, Star
} from 'lucide-react';

// Map icon names to components
const iconMap = {
  'play': Play,
  'sparkles': Sparkles,
  'zap': Zap,
  'heart': Heart,
  'trending-up': TrendingUp,
  'crown': Crown
};

// Tone-based gradient colors
const toneGradients = {
  'encouraging': 'from-blue-500/20 to-blue-600/5',
  'inspiring': 'from-[#D4AF37]/20 to-[#D4AF37]/5',
  'motivating': 'from-orange-500/20 to-orange-600/5',
  'gentle': 'from-purple-500/20 to-purple-600/5',
  'celebratory': 'from-green-500/20 to-green-600/5'
};

const toneBorders = {
  'encouraging': 'border-blue-500/30',
  'inspiring': 'border-[#D4AF37]/30',
  'motivating': 'border-orange-500/30',
  'gentle': 'border-purple-500/30',
  'celebratory': 'border-green-500/30'
};

const toneIconColors = {
  'encouraging': 'text-blue-400',
  'inspiring': 'text-[#D4AF37]',
  'motivating': 'text-orange-400',
  'gentle': 'text-purple-400',
  'celebratory': 'text-green-400'
};

const toneIconBg = {
  'encouraging': 'bg-blue-500/10',
  'inspiring': 'bg-[#D4AF37]/10',
  'motivating': 'bg-orange-500/10',
  'gentle': 'bg-purple-500/10',
  'celebratory': 'bg-green-500/10'
};

/**
 * JourneyCelebration - Premium celebration modal for completed journey
 * Elegant animation with strong emotional message
 */
const JourneyCelebration = ({ isOpen, onClose, userName }) => {
  const navigate = useNavigate();
  
  if (!isOpen) return null;
  
  // Elegant sparkle particles
  const particles = Array.from({ length: 20 }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    delay: Math.random() * 2,
    duration: 2 + Math.random() * 2,
    size: 4 + Math.random() * 8
  }));
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      >
        {/* Elegant floating particles */}
        {particles.map((particle) => (
          <motion.div
            key={particle.id}
            initial={{ opacity: 0, y: 100, x: `${particle.x}vw` }}
            animate={{ 
              opacity: [0, 1, 0],
              y: [-20, -150],
            }}
            transition={{
              duration: particle.duration,
              delay: particle.delay,
              repeat: Infinity,
              ease: "easeOut"
            }}
            className="absolute pointer-events-none"
            style={{ left: `${particle.x}%` }}
          >
            <Star 
              className="text-[#D4AF37]" 
              style={{ width: particle.size, height: particle.size }}
              fill="currentColor"
            />
          </motion.div>
        ))}
        
        {/* Main celebration card */}
        <motion.div
          initial={{ scale: 0.8, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.8, opacity: 0, y: 20 }}
          transition={{ type: "spring", stiffness: 200, damping: 20 }}
          onClick={(e) => e.stopPropagation()}
          className="relative max-w-md w-full bg-gradient-to-b from-[#1C1C1C] to-[#0A0A0A] border border-[#D4AF37]/30 rounded-3xl overflow-hidden shadow-2xl"
        >
          {/* Golden glow effect */}
          <div className="absolute inset-0 bg-gradient-to-b from-[#D4AF37]/10 to-transparent pointer-events-none" />
          
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 text-[#52525B] hover:text-white transition-colors z-10"
          >
            <X className="w-5 h-5" />
          </button>
          
          <div className="relative p-8 text-center">
            {/* Animated crown icon */}
            <motion.div
              initial={{ scale: 0, rotate: -20 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: "spring", stiffness: 200, delay: 0.2 }}
              className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-[#D4AF37] to-[#B8860B] rounded-full flex items-center justify-center shadow-lg shadow-[#D4AF37]/30"
            >
              <Crown className="w-10 h-10 text-white" />
            </motion.div>
            
            {/* Main message */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <h2 className="text-3xl font-serif text-white mb-3">
                Ta marque est née.
              </h2>
              <p className="text-[#A1A1AA] text-lg leading-relaxed mb-2">
                {userName ? `${userName}, tu` : 'Tu'} as accompli quelque chose de grand.
              </p>
              <p className="text-[#71717A] text-base">
                8 étapes. Une transformation complète.
              </p>
            </motion.div>
            
            {/* Stats badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.6 }}
              className="mt-6 inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full"
            >
              <CheckCircle className="w-4 h-4 text-green-400" />
              <span className="text-[#D4AF37] text-sm font-medium">Parcours complété à 100%</span>
            </motion.div>
            
            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="mt-8 space-y-3"
            >
              <button
                onClick={() => { navigate('/journey-evolution'); onClose(); }}
                className="w-full py-4 bg-gradient-to-r from-[#D4AF37] to-[#B8860B] text-black font-medium rounded-xl hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
              >
                <Sparkles className="w-5 h-5" />
                Découvrir mon évolution
              </button>
              <button
                onClick={() => { navigate('/my-revelations'); onClose(); }}
                className="w-full py-3 bg-transparent border border-[#27272A] text-white text-sm rounded-xl hover:bg-white/5 transition-colors"
              >
                Voir mes révélations
              </button>
            </motion.div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

/**
 * NotificationBanner - Premium, personalized dashboard notification
 * Shows ONE highly contextual notification with emotional impact
 */
export const NotificationBanner = ({ className = "" }) => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [notification, setNotification] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);

  const fetchNotifications = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/notifications/smart`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const journeyStats = response.data.journey_stats;
      setStats(journeyStats);
      
      // Check if journey just completed (8/8) and hasn't been celebrated yet
      if (journeyStats && journeyStats.completed_steps === 8) {
        const celebratedKey = `journey_celebrated_${journeyStats.user_name || 'user'}`;
        const hasCelebrated = localStorage.getItem(celebratedKey);
        if (!hasCelebrated) {
          setShowCelebration(true);
          localStorage.setItem(celebratedKey, 'true');
        }
      }
      
      if (response.data.primary) {
        setNotification(response.data.primary);
        // Mark as shown
        await axios.post(`${API}/notifications/mark-shown`, 
          { notification_id: response.data.primary.id },
          { headers: { Authorization: `Bearer ${token}` }}
        );
      }
    } catch (err) {
      console.error('Error fetching notifications:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const handleDismiss = async () => {
    if (notification && token) {
      try {
        await axios.post(`${API}/notifications/dismiss`,
          { notification_id: notification.id },
          { headers: { Authorization: `Bearer ${token}` }}
        );
      } catch (err) {
        console.error('Error dismissing notification:', err);
      }
    }
    setDismissed(true);
  };

  const handleAction = () => {
    if (notification?.cta_path) {
      navigate(notification.cta_path);
    }
  };

  if (loading || !notification || dismissed) {
    return (
      <>
        {/* Journey celebration modal - shown even when no notification */}
        <JourneyCelebration 
          isOpen={showCelebration} 
          onClose={() => setShowCelebration(false)}
          userName={stats?.user_name}
        />
      </>
    );
  }

  const IconComponent = iconMap[notification.icon] || Sparkles;
  const gradient = toneGradients[notification.tone] || toneGradients['inspiring'];
  const borderColor = toneBorders[notification.tone] || toneBorders['inspiring'];
  const iconColor = toneIconColors[notification.tone] || toneIconColors['inspiring'];
  const iconBg = toneIconBg[notification.tone] || toneIconBg['inspiring'];

  return (
    <>
      {/* Journey celebration modal */}
      <JourneyCelebration 
        isOpen={showCelebration} 
        onClose={() => setShowCelebration(false)}
        userName={stats?.user_name}
      />
      
      <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -20, height: 0 }}
        animate={{ opacity: 1, y: 0, height: 'auto' }}
        exit={{ opacity: 0, y: -20, height: 0 }}
        className={`mb-6 ${className}`}
        data-testid="notification-banner"
      >
        <div className={`relative bg-gradient-to-r ${gradient} border ${borderColor} rounded-2xl overflow-hidden`}>
          {/* Subtle pattern overlay */}
          <div className="absolute inset-0 opacity-20" style={{
            backgroundImage: `radial-gradient(circle at 20% 50%, rgba(212,175,55,0.1) 0%, transparent 50%)`
          }} />
          
          <div className="relative p-6">
            {/* Main content */}
            <div className="flex items-start gap-5">
              {/* Animated Icon */}
              <motion.div 
                initial={{ scale: 0.8, rotate: -10 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: "spring", stiffness: 200 }}
                className={`w-14 h-14 rounded-2xl ${iconBg} border border-[#27272A] flex items-center justify-center flex-shrink-0`}
              >
                <IconComponent className={`w-7 h-7 ${iconColor}`} />
              </motion.div>

              {/* Content */}
              <div className="flex-1 min-w-0 pt-1">
                <h3 className="text-white font-serif text-xl mb-2">
                  {notification.title}
                </h3>
                <p className="text-[#A1A1AA] text-base leading-relaxed">
                  {notification.message}
                </p>
                
                {/* CTA Button */}
                <motion.button
                  whileHover={{ scale: 1.02, x: 4 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleAction}
                  className="mt-5 inline-flex items-center gap-2 px-6 py-3 bg-white text-black text-sm font-medium rounded-xl hover:bg-gray-100 transition-all shadow-lg shadow-white/10"
                  data-testid="notification-cta"
                >
                  {notification.cta}
                  <ChevronRight className="w-4 h-4" />
                </motion.button>
              </div>

              {/* Dismiss button */}
              <button
                onClick={handleDismiss}
                className="absolute top-4 right-4 p-2 text-[#52525B] hover:text-white transition-colors rounded-lg hover:bg-white/5"
                aria-label="Fermer"
                data-testid="dismiss-notification"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
    </>
  );
};

/**
 * NotificationBell - Bell icon with badge for header
 * Shows badge only when there's an active notification
 */
export const NotificationBell = ({ onClick }) => {
  const { token } = useAuth();
  const [hasNotification, setHasNotification] = useState(false);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    const checkNotifications = async () => {
      if (!token) return;
      
      try {
        const response = await axios.get(`${API}/notifications/smart`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setHasNotification(response.data.has_notification || false);
        setStats(response.data.journey_stats);
      } catch (err) {
        console.error('Error checking notifications:', err);
      }
    };

    checkNotifications();
    // Refresh every 5 minutes
    const interval = setInterval(checkNotifications, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [token]);

  return (
    <button
      onClick={onClick}
      className="relative p-2 text-[#71717A] hover:text-white transition-colors"
      aria-label="Notifications"
      data-testid="notification-bell"
    >
      <Bell className="w-5 h-5" />
      {hasNotification && (
        <motion.span
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="absolute top-1 right-1 w-2.5 h-2.5 bg-[#D4AF37] rounded-full"
        />
      )}
    </button>
  );
};

/**
 * NotificationCenter - Dropdown showing journey progress
 */
export const NotificationCenter = ({ isOpen, onClose }) => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [notification, setNotification] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (!token || !isOpen) return;

      try {
        const response = await axios.get(`${API}/notifications/smart`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setNotification(response.data.primary);
        setStats(response.data.journey_stats);
      } catch (err) {
        console.error('Error fetching notifications:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token, isOpen]);

  const handleNotificationClick = () => {
    if (notification?.cta_path) {
      navigate(notification.cta_path);
      onClose();
    }
  };

  if (!isOpen) return null;

  const IconComponent = notification ? (iconMap[notification.icon] || Sparkles) : Sparkles;
  const iconColor = notification ? (toneIconColors[notification.tone] || 'text-[#D4AF37]') : 'text-[#D4AF37]';

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 10, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 10, scale: 0.95 }}
        className="absolute right-0 top-full mt-2 w-80 md:w-96 bg-[#0A0A0A] border border-[#27272A] rounded-2xl shadow-2xl z-50 overflow-hidden"
        data-testid="notification-center"
      >
        {/* Header */}
        <div className="p-4 border-b border-[#1C1C1C] flex items-center justify-between">
          <h3 className="text-white font-medium">Ton parcours</h3>
          <button
            onClick={onClose}
            className="p-1 text-[#52525B] hover:text-white transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Journey progress */}
        {stats && (
          <div className="px-4 py-4 bg-[#111] border-b border-[#1C1C1C]">
            <div className="flex items-center justify-between mb-3">
              <span className="text-[#71717A] text-sm">Progression</span>
              <span className="text-[#D4AF37] font-medium">
                {stats.completed_steps}/{stats.total_steps} étapes
              </span>
            </div>
            <div className="h-2 bg-[#1C1C1C] rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${(stats.completed_steps / stats.total_steps) * 100}%` }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                className="h-full bg-gradient-to-r from-[#D4AF37] to-[#E5C585] rounded-full"
              />
            </div>
            {stats.completed_steps === 8 ? (
              <p className="text-green-400 text-xs mt-2 flex items-center gap-1">
                <CheckCircle className="w-3 h-3" />
                Parcours complété — Ta marque est née ✨
              </p>
            ) : stats.completed_steps === 7 ? (
              <p className="text-[#D4AF37] text-xs mt-2">
                Plus qu'une étape — Ta marque est sur le point de naître
              </p>
            ) : stats.completed_steps >= 5 ? (
              <p className="text-[#D4AF37] text-xs mt-2">
                Plus que {8 - stats.completed_steps} étapes — Ton identité prend forme
              </p>
            ) : stats.completed_steps >= 3 ? (
              <p className="text-[#A1A1AA] text-xs mt-2">
                {8 - stats.completed_steps} étapes restantes — Tu construis quelque chose de solide
              </p>
            ) : null}
          </div>
        )}

        {/* Current notification */}
        <div className="p-4">
          {loading ? (
            <div className="py-6 text-center">
              <div className="w-6 h-6 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          ) : notification ? (
            <motion.button
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              onClick={handleNotificationClick}
              className="w-full p-4 text-left bg-[#111] hover:bg-[#1C1C1C] border border-[#27272A] rounded-xl transition-colors"
            >
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-xl bg-[#1C1C1C] flex items-center justify-center flex-shrink-0`}>
                  <IconComponent className={`w-5 h-5 ${iconColor}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium mb-1">
                    {notification.title}
                  </p>
                  <p className="text-[#71717A] text-xs line-clamp-2">
                    {notification.message}
                  </p>
                  <span className="inline-flex items-center gap-1 mt-2 text-[#D4AF37] text-xs font-medium">
                    {notification.cta}
                    <ChevronRight className="w-3 h-3" />
                  </span>
                </div>
              </div>
            </motion.button>
          ) : (
            <div className="py-6 text-center">
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: "spring", stiffness: 200 }}
              >
                <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
              </motion.div>
              {/* Adaptive empty state based on progression */}
              {stats && stats.completed_steps >= 6 ? (
                <>
                  <p className="text-white font-medium mb-2">Tu es alignée. Et ça se voit ✨</p>
                  <p className="text-[#A1A1AA] text-sm leading-relaxed">
                    Continue — ta marque prend vie.
                  </p>
                </>
              ) : stats && stats.completed_steps >= 3 ? (
                <>
                  <p className="text-white font-medium mb-2">Tu avances bien 💫</p>
                  <p className="text-[#A1A1AA] text-sm leading-relaxed">
                    Continue sur ta lancée — chaque étape te rapproche de ta vision.
                  </p>
                </>
              ) : (
                <>
                  <p className="text-white font-medium mb-2">Tout est en ordre ✨</p>
                  <p className="text-[#A1A1AA] text-sm leading-relaxed">
                    Chaque pas compte. Tu es sur le bon chemin.
                  </p>
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-[#1C1C1C] bg-[#0A0A0A]">
          <button
            onClick={() => { navigate('/journey-evolution'); onClose(); }}
            className="w-full py-2 text-center text-[#D4AF37] text-sm font-medium hover:text-[#E5C585] transition-colors"
          >
            Voir mon évolution complète
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

/**
 * Hook to check if there's an active notification
 */
export const useNotificationCount = () => {
  const { token } = useAuth();
  const [hasNotification, setHasNotification] = useState(false);

  useEffect(() => {
    const check = async () => {
      if (!token) return;
      
      try {
        const response = await axios.get(`${API}/notifications/smart`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setHasNotification(response.data.has_notification || false);
      } catch (err) {
        console.error('Error:', err);
      }
    };

    check();
    const interval = setInterval(check, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [token]);

  return hasNotification ? 1 : 0;
};

export default NotificationBanner;
